import {
  Box,
  Button,
  Card,
  Divider,
  FormHelperText,
  Grid,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import CategoryField from "app/components/FieldComponent/Category";
import FormField from "app/components/FieldComponent/FormField";
import { UploadedFileCell } from "app/components/UploadedFileCell";
import { AudioImageModal } from "app/containers/DailyActivity/components/AudioImageModal";
import { ResourcePreview } from "app/containers/DailyActivity/components/ResourcePreview";
import { UploadField } from "app/containers/DailyActivity/components/UploadField";
import { UploadedResource } from "app/containers/DailyActivity/types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TRANSLATE } from "utils/helpers";
import { selectEditContent, selectList } from "../../redux/selector";
import { actions } from "../../redux/slice";
import { ContentResource } from "../../types";
import { useStyles } from "./styles";
import { isDuplicate, onValidate } from "./validator";

const AddTreatmentContent = ({
  treatmentId,
  openModal,
  handleModalClose,
  disable,
  mode,
  noofSession,
  editIndex,
}) => {
  const dispatch = useDispatch();
  const form = useSelector(selectEditContent);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const listitems = useSelector(selectList);
  const { treatment } = listitems;
  const styles = useStyles();

  const onChange = (key: string, value: string | number) => {
    if (key === "hours") {
      value = String(value).replace(/[^0-9]/g, "");
    }
    dispatch(actions.updateFormValue({ key: `content.${key}`, value }));
  };

  const onUploadSuccess = (resource: UploadedResource) => {
    dispatch(
      actions.updateFormUploadValue({
        resource: resource as ContentResource,
      })
    );
  };

  const removeResource = (resource: ContentResource) => {
    dispatch(actions.removeFormUploadValue({ resource }));
  };

  const handleSaveBtn = () => {
    const errors = onValidate(form);
    if (errors?.length > 0) {
      dispatch(actions.setFormErrors({ key: "content", errors }));
      return;
    }
    const exists = isDuplicate(form, treatment);
    if (exists) {
      dispatch(
        actions.setFormErrors({
          key: "content",
          errors: [
            {
              name: "name",
              error: TRANSLATE("TREATMENT_TYPES.TREATMENT_CONTENT_EXISTS"),
            },
          ],
        })
      );
      return;
    }
    const resources = (form.resources ?? [])
      .filter((r) => !r.isDeleted && r.resourcePath)
      .slice(0, 1);
    const session_id = form.session_id.value;
    const basePayload = {
      treatmentId: treatmentId,
      name: form.name.value,
      beforeAfter: form.beforeAfter.value,
      hours: form.hours.value,
      at: form.beforeAfter.value,
      session_number: session_id,
      session: Number(session_id) > 1 ? "multi session" : "single session",
      session_id: session_id,
      duration: form.hours.value,
      description: form.description.value,
      shortDescription: form.shortDescription.value,
      category: form.category.value,
      resources,
    };
    if (mode === "edit") {
      dispatch(
        actions.doaddTreatmentContentItem({
          ...basePayload,
          id: form.id?.value,
          index: editIndex,
          type: "edit",
        })
      );
    } else {
      dispatch(
        actions.doaddTreatmentContentItem({
          ...basePayload,
          id: 0,
          type: "add",
        })
      );
    }
  };

  const uploadedFiles = (form.resources ?? [])
    .filter((f) => !f.isDeleted && f.resourcePath)
    .slice(0, 1);

  const audioResource = uploadedFiles.find((f) => f.resourceType === "audio");

  const [audioModalOpen, setAudioModalOpen] = useState(false);

  const handleSaveAudioImage = (value: {
    thumbnailPath?: string;
    thumbnailName?: string;
  }) => {
    dispatch(actions.setAudioThumbnail(value));
  };

  const beforeAfterOptions = [
    { value: "Before", label: "Before" },
    { value: "After", label: "After" },
  ];
  const sessionOptions = Array.from({ length: noofSession || 1 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  return (
    <Modal open={openModal} onClose={() => {}}>
      <Card
        className={styles.addModal}
        sx={{ direction: theme.direction }}
      >
        <div className={styles.modalContainer}>
          <Grid>
            <Typography align="center" className={styles.headerModal}>
              {mode === "add" || mode === "new"
                ? t("TREATMENT_TYPES.ADD")
                : t("TREATMENT_TYPES.EDIT")}{" "}
              {t("TREATMENT_TYPES.TREATMENT_CONTENT")}
            </Typography>
          </Grid>

          <Grid container spacing={2} className={styles.bodyContainer}>
            <Grid item xs={12} md={8} className={styles.leftColumn}>
              <Grid container columnSpacing={2} rowSpacing={1}>
                <FormField
                  type="text"
                  xs={5}
                  required
                  label={t("TREATMENT_TYPES.NAME")}
                  placeholder={t("TREATMENT_TYPES.ENTER_NAME")}
                  value={form.name.value}
                  error={form.name.error}
                  onChange={(v) => onChange("name", v)}
                />
                <FormField
                  type="select"
                  xs={2.5}
                  required
                  label={t("TREATMENT_TYPES.BEFORE_AFTER")}
                  selectPlaceholder="Choose"
                  options={beforeAfterOptions}
                  value={form.beforeAfter.value}
                  error={form.beforeAfter.error}
                  disabled={disable}
                  onChange={(v) => onChange("beforeAfter", v)}
                />
                <FormField
                  type="number"
                  xs={2}
                  required
                  label={t("TREATMENT_TYPES.HOURS")}
                  placeholder={"Enter hours"}
                  value={form.hours.value}
                  error={form.hours.error}
                  onChange={(v) => onChange("hours", v)}
                />
                <FormField
                  type="select"
                  xs={2.5}
                  required
                  label={t("TREATMENT_TYPES.SESSION_NUMBER")}
                  selectPlaceholder="Choose"
                  options={sessionOptions}
                  value={form.session_id.value}
                  error={form.session_id.error}
                  disabled={disable}
                  onChange={(v) => onChange("session_id", v)}
                />
                <FormField
                  type="text"
                  xs={12}
                  required
                  label={t("TREATMENT_TYPES.SHORT_DESCRIPTION")}
                  placeholder={t("TREATMENT_TYPES.ENTER_SHORT_DESCRIPTION")}
                  value={form.shortDescription.value}
                  error={form.shortDescription.error}
                  onChange={(v) => onChange("shortDescription", v)}
                />
                <FormField
                  type="textarea"
                  xs={12}
                  rows={6}
                  required
                  label={t("TREATMENT_TYPES.DESCRIPTION")}
                  placeholder={"Enter Description"}
                  value={form.description.value}
                  error={form.description.error}
                  onChange={(v) => onChange("description", v)}
                />
              </Grid>
            </Grid>

            <Grid item xs="auto" className={styles.dividerWrap}>
              <Divider
                orientation="vertical"
                flexItem
                className={styles.divider}
              />
            </Grid>

            <Grid item xs={12} md={true} className={styles.rightColumn}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Grid container>
                  <CategoryField
                    value={form.category.value}
                    xs={12}
                    changeValue={(value) => onChange("category", value)}
                    disabled={disable}
                    error={form.category.error}
                  />
                </Grid>

                <Box className={styles.uploadsRight}>
                  {uploadedFiles.length ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {uploadedFiles.map((f) => (
                        <UploadedFileCell
                          key={`${f.resourceType}-${f.resourcePath}`}
                          resources={[f]}
                          onDelete={() => removeResource(f)}
                          className={styles.uploadedFileCell}
                        />
                      ))}
                    </Box>
                  ) : (
                    <UploadField onUpload={onUploadSuccess} />
                  )}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <label className={styles.label}>
                    {t("TREATMENT_TYPES.UPLOADED_FILE")}
                  </label>
                  <Box className={styles.uploadedPanel}>
                    {uploadedFiles.length ? (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: "flex",
                          gap: 1,
                          width: "100%",
                        }}
                      >
                        {uploadedFiles.map((f) => (
                          <Box
                            key={`${f.resourceType}-${f.resourcePath}`}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              minHeight: 0,
                              display: "flex",
                            }}
                          >
                            <ResourcePreview
                              resource={f}
                              onEditImage={
                                f.resourceType === "audio"
                                  ? () => setAudioModalOpen(true)
                                  : undefined
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography className={styles.noFile}>
                        {t("TREATMENT_TYPES.NO_FILE_YET")}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ direction: i18n.dir(), width: "100%" }}>
                  {form.resourceError?.error ? (
                    <FormHelperText error className={styles.uploadsError}>
                      {form.resourceError.error}
                    </FormHelperText>
                  ) : null}
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            className={styles.actionsRow}
          >
            <Button
              className={styles.cancelBtn}
              variant="outlined"
              onClick={handleModalClose}
            >
              {t("BUTTONS.CANCEL")}
            </Button>
            <Button
              className={styles.saveBtn}
              variant="contained"
              onClick={handleSaveBtn}
            >
              {mode === "edit" ? t("BUTTONS.SAVE") : t("TREATMENT_TYPES.ADD")}
            </Button>
          </Grid>
        </div>
        <AudioImageModal
          open={audioModalOpen}
          initialThumbnailPath={audioResource?.thumbnailPath}
          initialThumbnailName={audioResource?.thumbnailName}
          initialResourcePath={audioResource?.resourcePath}
          initialResourceUrl={audioResource?.url}
          onClose={() => setAudioModalOpen(false)}
          onSave={handleSaveAudioImage}
        />
      </Card>
    </Modal>
  );
};
export default AddTreatmentContent;
